// src/pages/inventory/StockDetails.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaBoxes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaLock,
  FaGasPump,
  FaBuilding,
  FaHistory,
  FaPrint,
  FaDownload,
  FaCalendarAlt,
  FaDollarSign,
  FaWarehouse,
  FaUser,
  FaInfoCircle,
  FaChartBar,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { stockApi } from "../../api/tenant/stock.api";
import "../../styles/styles.css";

const StockDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [stock, setStock] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadStockDetails();
  }, [id]);

  const loadStockDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const [stockRes, transRes] = await Promise.all([
        stockApi.getById(id),
        stockApi.getTransactions({ stockId: id, limit: 50 }),
      ]);

      const stockData = stockRes?.data?.data || stockRes?.data || null;
      setStock(stockData);

      const transData = transRes?.data?.data?.data || transRes?.data?.data || transRes?.data || [];
      setTransactions(Array.isArray(transData) ? transData : []);

      // Calculate stock stats
      if (stockData) {
        const totalStock = (stockData.filled_stock || 0) + (stockData.empty_stock || 0) + 
                          (stockData.damaged_stock || 0) + (stockData.reserved_stock || 0) + (stockData.scrap_stock || 0);
        const availableStock = (stockData.filled_stock || 0) + (stockData.empty_stock || 0);
        const isLow = availableStock <= stockData.reorder_level;
        const isCritical = availableStock <= stockData.min_stock_level;

        setStats({
          totalStock,
          availableStock,
          isLow,
          isCritical,
          stockValue: stockData.stock_value || 0,
          avgCost: stockData.unit_cost || 0,
        });
      }
    } catch (error) {
      console.error("Failed to load stock details:", error);
      setError(error.response?.data?.message || "Failed to load stock details");
      toast.error("Failed to load stock details");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete stock for "${stock?.cylinder_type_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await stockApi.delete(id);
        toast.success("Stock deleted successfully!");
        navigate("/inventory/stock");
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete stock");
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getStatusBadge = () => {
    if (!stats) return null;
    if (stats.isCritical) {
      return <Badge bg="danger" className="px-3 py-2">Critical Stock</Badge>;
    }
    if (stats.isLow) {
      return <Badge bg="warning" className="px-3 py-2">Low Stock</Badge>;
    }
    return <Badge bg="success" className="px-3 py-2">Healthy Stock</Badge>;
  };

  const getStockTypeBadge = (type) => {
    const configs = {
      purchase: { bg: "success", label: "Purchase" },
      sale: { bg: "primary", label: "Sale" },
      return: { bg: "info", label: "Return" },
      damage: { bg: "danger", label: "Damage" },
      scrap: { bg: "dark", label: "Scrap" },
      reserve: { bg: "warning", label: "Reserve" },
      unreserve: { bg: "secondary", label: "Unreserve" },
    };
    const config = configs[type] || { bg: "secondary", label: type };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
        <div className="text-center py-5">
          <Spinner animation="border" variant="secondary" size="lg" />
          <h4 className="mt-3">Loading stock details...</h4>
        </div>
      </Container>
    );
  }

  if (!stock) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
        <Alert variant="danger">
          <FaInfoCircle className="me-2" /> Stock not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate("/inventory/stock")}>
          <FaArrowLeft className="me-2" /> Back to Stock
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/inventory/stock")}
            className="mb-2"
            style={{ borderRadius: "8px", fontSize: "14px", padding: "8px 16px" }}
          >
            <FaArrowLeft className="me-2" /> Back to Stock
          </Button>
          <h2 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
            <FaBoxes className="me-2" /> Stock Details
          </h2>
          <p className="text-muted">
            {stock.cylinder_type_name} - {stock.brand_name || "No Brand"}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={handlePrint}
            className="d-flex align-items-center gap-2"
          >
            <FaPrint /> Print
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate(`/inventory/stock/edit/${id}`)}
            className="d-flex align-items-center gap-2"
            style={{ borderColor: "#ff9800", color: "#ff9800" }}
          >
            <FaEdit /> Edit
          </Button>
          <Button
            variant="outline-danger"
            onClick={handleDelete}
            className="d-flex align-items-center gap-2"
          >
            <FaTrash /> Delete
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3">
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Total Stock</small>
              <h4 className="fw-bold mb-0">{stats?.totalStock || 0}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #027A48" }}>
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Available</small>
              <h4 className="fw-bold mb-0" style={{ color: "#027A48" }}>{stats?.availableStock || 0}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #FED229" }}>
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Stock Value</small>
              <h4 className="fw-bold mb-0" style={{ color: "#FED229" }}>₹{formatCurrency(stats?.stockValue || 0)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #437EF7" }}>
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Avg Cost</small>
              <h4 className="fw-bold mb-0" style={{ color: "#437EF7" }}>₹{formatCurrency(stats?.avgCost || 0)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #E11D48" }}>
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Status</small>
              <div>{getStatusBadge()}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #6B7280" }}>
            <Card.Body className="py-2 px-3">
              <small className="text-muted">Last Updated</small>
              <div className="fw-bold" style={{ fontSize: "14px" }}>{formatDate(stock.updated_at)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card className="border-0 shadow-sm rounded-3">
        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="border-bottom px-3 pt-2"
            style={{ backgroundColor: "#f8f9fa", borderTopLeftRadius: "0.75rem", borderTopRightRadius: "0.75rem" }}
          >
            <Tab eventKey="overview" title={<span><FaInfoCircle className="me-2" />Overview</span>}>
              <div className="p-4">
                <Row>
                  <Col md={6}>
                    <InfoCard title="Product Information" icon={<FaGasPump />}>
                      <InfoRow label="Cylinder Type" value={stock.cylinder_type_name || "N/A"} highlight />
                      <InfoRow label="Brand" value={stock.brand_name || "N/A"} />
                      <InfoRow label="Batch Number" value={stock.batch_number || "N/A"} />
                      <InfoRow label="Manufacture Date" value={formatDate(stock.manufacture_date)} />
                      <InfoRow label="Expiry Date" value={formatDate(stock.expiry_date)} />
                    </InfoCard>
                  </Col>
                  <Col md={6}>
                    <InfoCard title="Stock Details" icon={<FaBoxes />}>
                      <InfoRow label="Opening Stock" value={stock.opening_stock || 0} />
                      <InfoRow label="Received Stock" value={stock.received_stock || 0} />
                      <InfoRow label="Delivered Stock" value={stock.delivered_stock || 0} />
                      <InfoRow label="Returned Stock" value={stock.returned_stock || 0} />
                      <InfoRow label="Closing Stock" value={stock.closing_stock || 0} />
                    </InfoCard>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md={6}>
                    <InfoCard title="Stock Breakdown" icon={<FaChartBar />}>
                      <Row className="g-2">
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#ECFDF3" }}>
                            <div className="fw-bold" style={{ color: "#027A48" }}>{stock.filled_stock || 0}</div>
                            <small className="text-muted">Filled</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#FEF6D7" }}>
                            <div className="fw-bold" style={{ color: "#FED229" }}>{stock.empty_stock || 0}</div>
                            <small className="text-muted">Empty</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#FFE4E6" }}>
                            <div className="fw-bold" style={{ color: "#E11D48" }}>{stock.damaged_stock || 0}</div>
                            <small className="text-muted">Damaged</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#D3EAFF" }}>
                            <div className="fw-bold" style={{ color: "#437EF7" }}>{stock.reserved_stock || 0}</div>
                            <small className="text-muted">Reserved</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#F1F3F5" }}>
                            <div className="fw-bold" style={{ color: "#6B7280" }}>{stock.scrap_stock || 0}</div>
                            <small className="text-muted">Scrap</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="p-2 rounded text-center" style={{ backgroundColor: "#D3EAFF" }}>
                            <div className="fw-bold" style={{ color: "#437EF7" }}>{stock.received_stock || 0}</div>
                            <small className="text-muted">Received</small>
                          </div>
                        </Col>
                      </Row>
                    </InfoCard>
                  </Col>
                  <Col md={6}>
                    <InfoCard title="Stock Limits & Location" icon={<FaWarehouse />}>
                      <InfoRow label="Min Stock Level" value={stock.min_stock_level || 10} />
                      <InfoRow label="Max Stock Level" value={stock.max_stock_level || 1000} />
                      <InfoRow label="Reorder Level" value={stock.reorder_level || 50} />
                      <InfoRow label="Reorder Quantity" value={stock.reorder_quantity || 100} />
                      <InfoRow label="Location" value={stock.location || "N/A"} />
                      <InfoRow label="Rack Number" value={stock.rack_number || "N/A"} />
                    </InfoCard>
                  </Col>
                </Row>
              </div>
            </Tab>

            <Tab eventKey="transactions" title={<span><FaHistory className="me-2" />Transactions</span>}>
              <div className="p-4">
                <div className="table-responsive">
                  <Table hover className="mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Date & Time</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Previous</th>
                        <th>New</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length > 0 ? (
                        transactions.map((txn) => (
                          <tr key={txn.id}>
                            <td style={{ fontSize: "12px" }}>{formatDateTime(txn.created_at)}</td>
                            <td>{getStockTypeBadge(txn.transaction_type)}</td>
                            <td><strong>{txn.quantity}</strong></td>
                            <td>{txn.previous_stock}</td>
                            <td>{txn.new_stock}</td>
                            <td>₹{formatCurrency(txn.unit_price)}</td>
                            <td>₹{formatCurrency(txn.total_amount)}</td>
                            <td style={{ fontSize: "12px" }}>{txn.remarks || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <FaHistory size={30} className="text-muted mb-2" />
                            <p className="text-muted mb-0">No transactions found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

// ==============================================
// Helper Components
// ==============================================

const InfoCard = ({ title, icon, children }) => (
  <Card className="border-0 shadow-sm rounded-3 h-100">
    <Card.Header className="bg-white border-bottom pt-3 pb-2">
      <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
        {icon} {title}
      </h6>
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

const InfoRow = ({ label, value, highlight }) => (
  <div className="mb-2 pb-2 border-bottom">
    <small className="text-muted">{label}</small>
    <div className={highlight ? "fw-bold fs-6" : ""} style={highlight ? { color: "rgb(30, 58, 111)" } : {}}>
      {value}
    </div>
  </div>
);

export default StockDetails;